/**
 * RoofSnapshotConfirm · iter303ak-fix2 — Quote Sent · Task 1 · Action 1
 * ────────────────────────────────────────────────────────────────────
 * Merged screenshot + template confirm card. Replaces the previous
 * two-action pattern (roof_screenshot_capture → roof_template_pick)
 * with a single unified card that shows:
 *   • The current roof screenshot IMAGE inline (via /photos/by-id →
 *     photo.url on the backend) + a Change button that opens the
 *     existing RoofDiagramPicker
 *   • The current roof template thumbnail + a Change button that
 *     navigates to the templates picker
 *
 * Nothing new — this is 100% orchestration on top of RoofDiagramPicker
 * and the existing property.roof_template_id field.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, ExternalLink, CheckCircle2, AlertCircle } from 'lucide-react';
import RoofDiagramPicker from '@/components/RoofDiagramPicker';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function RoofSnapshotConfirm({ action, capturedData, onNext, onBack, onCancel, canBack }) {
  const params = action.params || {};
  const propertyId = capturedData?.property_id;
  const [prop, setProp] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!propertyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}`);
      setProp(data);
      const pid = data?.roof_screenshot_photo_id || data?.roof_diagram_photo_id;
      if (pid) {
        try {
          const { data: photo } = await api.get(`/photos/by-id/${pid}`);
          setPhotoUrl(photo?.url ? `${process.env.REACT_APP_BACKEND_URL}${photo.url}` : '');
        } catch { setPhotoUrl(''); }
      } else {
        setPhotoUrl('');
      }
      const tid = data?.roof_template_id;
      if (tid) {
        try {
          // iter303ak-fix3 · Correct endpoint is /roof-templates/{id}
          // (NOT roof-design-templates — that's a separate collection).
          // The property links to roof_templates via roof_template_id.
          const { data: tpl } = await api.get(`/roof-templates/${tid}`);
          setTemplateName(tpl?.name || `Template ${String(tid).slice(-6)}`);
        } catch {
          setTemplateName(`Template ${String(tid).slice(-6)}`);
        }
      } else {
        setTemplateName('');
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const persistScreenshot = async (photoId) => {
    setSaving(true);
    try {
      await api.patch(`/properties/${propertyId}`, { roof_screenshot_photo_id: photoId });
      await load();
      setPickerOpen(false);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save roof screenshot');
    } finally { setSaving(false); }
  };

  const hasScreenshot = !!(prop?.roof_screenshot_photo_id || prop?.roof_diagram_photo_id);
  const hasTemplate = !!prop?.roof_template_id;
  const canContinue = hasScreenshot && hasTemplate;
  const address = prop ? [prop.property_address_num || prop.address_num, prop.property_address_street || prop.address_street, prop.property_address_suburb || prop.address_suburb, prop.property_address_city || prop.address_city].filter(Boolean).join(', ') : '';
  const earthUrl = address
    ? `https://earth.google.com/web/search/${encodeURIComponent(address)}`
    : 'https://earth.google.com/web/';

  if (!propertyId) {
    return (
      <div className="p-6 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
        No property linked to this run — cannot confirm roof snapshot.
        {canBack && (<button onClick={onBack} className="ml-3 text-rose-800 underline">← Back</button>)}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="wf-roof-snapshot-confirm">
      <div className="rounded-xl border border-orange-200 bg-orange-50/60 p-4">
        <div className="flex items-start gap-3">
          <ImageIcon className="w-5 h-5 text-orange-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-orange-900">Roof Snapshot + Template Confirm</div>
            <div className="text-sm text-orange-800 mt-1">
              {params.prompt || 'Confirm the roof screenshot and template are current. Change either if it needs re-doing.'}
            </div>
            <a href={earthUrl} target="_blank" rel="noopener noreferrer"
               className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-orange-700 hover:text-orange-900 underline decoration-dotted"
               data-testid="wf-snapshot-earth-link">
              <ExternalLink size={12} /> Open Google Earth at this property
            </a>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-slate-400 italic">Loading property…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Screenshot column */}
          <div className="border-2 border-slate-200 rounded-lg overflow-hidden bg-white">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Roof Screenshot</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {hasScreenshot ? <span className="text-emerald-700 font-medium">On file ✓</span> : <span className="text-amber-600 font-medium">Missing</span>}
                </div>
              </div>
              <button
                onClick={() => setPickerOpen(o => !o)}
                data-testid="wf-snapshot-toggle-picker"
                className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded hover:bg-orange-700">
                {pickerOpen ? 'Close' : (hasScreenshot ? 'Change' : '+ Add')}
              </button>
            </div>
            {pickerOpen ? (
              <div className="p-4 bg-slate-50">
                <RoofDiagramPicker
                  api={api}
                  propertyId={propertyId}
                  selectedId={prop?.roof_screenshot_photo_id || prop?.roof_diagram_photo_id || ''}
                  onSelect={persistScreenshot}
                  onClose={() => setPickerOpen(false)}
                />
                {saving && <div className="mt-2 text-xs text-slate-500 italic">Saving…</div>}
              </div>
            ) : (
              <div className="p-4">
                {hasScreenshot && photoUrl ? (
                  <img src={photoUrl} alt="Roof screenshot"
                       className="w-full h-52 object-cover rounded bg-slate-50 border border-slate-100"
                       data-testid="wf-snapshot-preview-img"
                       onError={(e) => { e.target.style.display = 'none'; }} />
                ) : hasScreenshot ? (
                  <div className="w-full h-52 rounded bg-slate-50 border border-slate-100 flex items-center justify-center text-xs text-slate-400">Loading preview…</div>
                ) : (
                  <div className="w-full h-52 rounded bg-amber-50 border border-amber-200 flex items-center justify-center text-xs text-amber-700 gap-1">
                    <AlertCircle size={14} /> No screenshot yet — click Add above.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Template column */}
          <div className="border-2 border-slate-200 rounded-lg overflow-hidden bg-white">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Roof Template</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {hasTemplate
                    ? <span className="text-emerald-700 font-medium">On file ✓</span>
                    : <span className="text-amber-600 font-medium">Missing</span>}
                </div>
              </div>
              <button
                onClick={() => navigate(`/appraisals/${capturedData?.appraisal_id || ''}?tab=Overview&focus=template`)}
                disabled={!capturedData?.appraisal_id}
                data-testid="wf-snapshot-template-change"
                className="px-3 py-1 bg-emerald-600 text-white text-xs font-semibold rounded hover:bg-emerald-700 disabled:opacity-40">
                {hasTemplate ? 'Change' : '+ Pick'}
              </button>
            </div>
            <div className="p-4">
              {hasTemplate ? (
                <div className="w-full h-52 rounded bg-white border border-emerald-200 relative overflow-hidden">
                  {/* iter303ak-fix3 · Actually render the template image.
                      The /roof-templates/{id}/image endpoint returns the
                      PNG (or SVG) that represents the traced roof shape. */}
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/roof-templates/${prop.roof_template_id}/image`}
                    alt={templateName || 'Roof template'}
                    className="w-full h-full object-contain bg-white"
                    data-testid="wf-snapshot-template-preview-img"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-emerald-50/95 border-t border-emerald-200 text-emerald-900">
                    <div className="text-xs font-semibold truncate" data-testid="wf-snapshot-template-name">{templateName}</div>
                  </div>
                </div>
              ) : (
                <div className="w-full h-52 rounded bg-amber-50 border border-amber-200 flex items-center justify-center text-xs text-amber-700 gap-1">
                  <AlertCircle size={14} /> No template — click Pick above.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 pt-2">
        {canBack && (
          <button onClick={onBack} data-testid="wf-snapshot-back"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-snapshot-cancel"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        <div className="text-xs text-slate-500 mr-2">
          {canContinue
            ? <span className="text-emerald-700 font-medium">Both on file — ready to continue</span>
            : <span className="text-amber-600">Screenshot + template both required</span>}
        </div>
        <button
          onClick={onNext}
          disabled={!canContinue || saving}
          data-testid="wf-snapshot-next"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40 inline-flex items-center gap-2">
          <CheckCircle2 size={14} /> Continue → saved: screenshot + template
        </button>
      </div>
    </div>
  );
}
